import {
  Body,
  Grid,
  GridColumn,
  useSnackbar,
  Switch,
  IconButton
} from "@livingdesign/react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { getService, patchService } from "../../../axios/axios";
import Label from "../../../components/TextField/Label/Label";
import { ORGANIZATION_SERVICE } from "../../../constants/baseURLs";
import {
  ADDRESS_LINE_1, ADDRESS_LINE_2, ADDRESS_TYPE, BILLING_CONTACT_NAME, CITY, COUNTRY, EXPERTISE, FIRM_FAX_NO,
  FIRM_PHONE_NO, MEL_APPROVED,
  SELECT_EXPERTISE_FIRST,
  RELATIONSHIP_PARTNER_EMAIL, RELATIONSHIP_PARTNER_NAME, SELECT_ADDRESS, SELECT_EXPERTISE, SELECT_STATE, STATE, TAX_ID, WEBSITE, ZIPCODE,
  INTERNAL_CONTACT_NAME,
  INTERNAL_CONTACT_EMAIL,
  SELECT_COUNTRY,
  PRIMARY_EXPERTISE,
  SELECT_FIRM_SIZE,
  FIRM_SIZE,
  YES,
  NO,
  SELECT_REGION,
  STATE_PRACTICES,
  OFFICE_LOCATION,
  ADD_NEW_STATE_PRACTICE,
  ADD_NEW_OFFICE_LOCATION,
} from "../../../constants/constants";
import { ORGANIZATION_UPDATE_MSG } from "../../../constants/messages";
import { setInputData, setOrgDetailValues } from "../../../store/actions/organizationAction";
import Detail from "../../Detail/Detail";
import OrganizationDetailStyles from "./EditOrganization.styles";
import MuiValidationMessage from "../../../Validation/MuiValidationMessage/MuiValidationMessage";
import { organizationValidationRules } from "../../../Validation/validationRules";
import { getErrorMessage } from "../../../helpers/utils";
import { filterPayload, formatPhoneNumber } from "../../../helpers/commonUtils";
import MasterDataAutocomplete from "../../../components/Autocomplete/MasterDataAutocomplete/MasterDataAutocomplete";
import MasterDataMultiAutocomplete from "../../../components/Autocomplete/MasterDataMultiAutocomplete/MasterDataMultiAutocomplete";
import { TextField, MenuItem, Select } from "@mui/material";
import { convertToUTCDateTime } from "../../../helpers/utcUtils";
import { DATE_YYYY_MM_DD, TIME_HH_MM_SS_A } from "../../../constants/dateFormats";
import moment from "moment";
import { Trash } from "@livingdesign/icons";
import MuiDataTable from "../../../components/Table/MuiDataTable/MuiDataTable";

/**
 * EditOrganization component used to update details of an existing organization.
 *
 * @author [AKASH D R]
 */
const EditOrganization = (props) => {
  const classes = OrganizationDetailStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { addSnack } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const organizationDetailValues = useSelector((state) => state?.organization?.organizationDetailValues);
  const [data] = useState(organizationDetailValues);
  const { register, formState: { errors, isValid } } = useForm({ mode: "all" });
  const [editValues, setEditValues] = useState({});

  //destructure organizationDetailValues
  const {
    identifier,
    companyName,
    orgType,
    orgSubType,
    addressType,
    addressLine1,
    addressLine2,
    city,
    state,
    country,
    email,
    website,
    phoneNo,
    faxNo,
    comments,
    wIREFirm,
    fCPAApproved,
    mELApproved,
    hostId,
    sAPId,
    cZId,
    cZSuffix,
    expertise,
    permittedAdverseRepresentation,
    limitedUseFirm,
    conflictWaiver,
    vendorId,
    contactReferenceId,
    competitorNotice,
    taxId,
    billingContactName,
    relationshipPartnerName,
    relationshipPartnerEmail,
    zipCode,
    primaryExpertise,
    firmSize,
    internalContactName,
    internalContactEmail,
    officeLocationCity,
    officeLocationState,
    officeLocationCountry,
    statePracticesCountry,
    statePracticesState,
    statePracticesRegion,
    officeLocations,
    statePractices
  } = organizationDetailValues;
  const [expertiseArray, setExpertiseArray] = useState(expertise === '' ? [] : expertise?.split(','));

  const [officeLocationsData, setOfficeLocationsData] = useState([]);
  const [statePracticesData, setStatePracticesData] = useState([]);

  const customTableOptions = {
    rowsPerPage: 15,
    rowsPerPageOptions: [15, 25, 50],
    selectableRows: 'none',
    responsive: 'standard',
    elevation: 0,
  };

  useEffect(() => {
    setOfficeLocations();
    setStatePractices();
  }, []);

  const getOrganizationData = () => {
    setLoading(true)
    getService(ORGANIZATION_SERVICE, `/organizations/v1/${identifier}`)
      .then((res) => {
        const detail = res?.data?.result?.organization;
        dispatch(setOrgDetailValues(detail));
      })
      .catch((error) => {
        addSnack({
          message: getErrorMessage(error)
        });
      })
      .finally(() => {
        setLoading(false)
        history.push(`/organization`)
      });
  }

  const setOfficeLocations = () => {
    if (officeLocations) {
      try {
        const locations = JSON.parse(officeLocations).map((location, index) => ({
          id: index,
          country: location.country || '',
          state: location.state || '',
          city: location.city || '',
          countryCode: location.countryCode || 346
        }));
        setOfficeLocationsData(locations);
      } catch (error) {
        setOfficeLocationsData([]);
      }
    } else if (officeLocationCountry) {
      setOfficeLocationsData([{
        id: 0,
        country: officeLocationCountry,
        state: officeLocationState,
        city: officeLocationCity,
        countryCode: 346
      }]);
    } else {
      setOfficeLocationsData([]);
    }
  };

  const setStatePractices = () => {
    if (statePractices) {
      try {
        const practices = JSON.parse(statePractices).map((practice, index) => ({
          id: index,
          country: practice.country || '',
          state: practice.state || '',
          region: Array.isArray(practice.region)
            ? practice.region
            : (practice.region ? practice.region.split(",") : []),
          countryCode: practice.countryCode || 346
        }));
        setStatePracticesData(practices);
      } catch (error) {
        setStatePracticesData([]);
      }
    } else if (statePracticesCountry) {
      setStatePracticesData([{
        id: 0,
        country: statePracticesCountry,
        state: statePracticesState,
        region: Array.isArray(statePracticesRegion)
          ? statePracticesRegion
          : (statePracticesRegion ? statePracticesRegion.split(",") : []),
        countryCode: 346
      }]);
    } else {
      setStatePracticesData([]);
    }
  }

  /*
  * Function would be called on Change of Form Fields
  */
  const handleInputChange = (name, value) => {
    dispatch(setInputData(name, value))
    setEditValues({ ...editValues, [name]: value })
  }

  /**
   * This function will use to handle the multiple expertise
  */
  const multiSelectHandler = (e, value) => {
    const selectedOptions = (typeof value === 'string' ? value?.split(',') : value);
    setExpertiseArray(selectedOptions)
    dispatch(setInputData('expertise', selectedOptions?.toString()))
    setEditValues({ ...editValues, 'expertise': value?.toString() })
  }

  /*
  * Function would be called on Click of Cancel button
  */
  const handleCancel = () => {
    dispatch(setOrgDetailValues(data))
    history.push(`/organization`)
  };


  /*
  * Function would be called on Click of Update Button
  */
  const handleUpdate = () => {
    const body = {
      companyName,
      orgType,
      orgSubType,
      addressType,
      addressLine1,
      addressLine2,
      city,
      state,
      country,
      email: editValues?.email,
      website: editValues?.website,
      phoneNo: editValues?.phoneNo,
      faxNo: editValues?.faxNo,
      expertise: editValues?.expertise,
      primaryExpertise: editValues?.primaryExpertise,
      zipCode: editValues?.zipCode,
      comments,
      wIREFirm,
      fCPAApproved,
      mELApproved,
      hostId,
      sAPId,
      cZId,
      cZSuffix,
      permittedAdverseRepresentation,
      limitedUseFirm,
      conflictWaiver,
      vendorId,
      contactReferenceId,
      competitorNotice,
      taxId,
      billingContactName,
      relationshipPartnerName,
      relationshipPartnerEmail,
      updatedDate: convertToUTCDateTime({ date: moment().format(DATE_YYYY_MM_DD), time: moment().format(TIME_HH_MM_SS_A) }),
      version: "1.0",
      firmSize,
      officeLocations: JSON.stringify((officeLocationsData).map((location) => ({
        country: location.country,
        state: location.state,
        city: location.city,
      }))),
      statePractices: JSON.stringify(statePracticesData.map((practice) => ({
        country: practice.country,
        state: practice.state,
        region: practice.region,
      })))
    }
    const filteredBody = filterPayload(body)
    filteredBody.wIREFirm = wIREFirm;
    setLoading(true)
    patchService(ORGANIZATION_SERVICE, `/organizations/v1/${identifier}`, filteredBody)
      .then((res) => {
        setLoading(false);
        addSnack({
          message: ORGANIZATION_UPDATE_MSG,
        });
        getOrganizationData();
      })
      .catch((error) => {
        setLoading(false);
        addSnack({
          message: getErrorMessage(error)
        });
      });
  };

  const isDirty = (companyName !== "" && (addressType !== data?.addressType || addressLine1 !== data?.addressLine1 || firmSize !== data?.firmSize ||
    addressLine2 !== data?.addressLine2 || city !== data?.city || state !== data?.state || country !== data?.country || email !== data?.email ||
    website !== data?.website || phoneNo !== data?.phoneNo || faxNo !== data?.faxNo || expertise !== data?.expertise || primaryExpertise !== data?.primaryExpertise ||
    taxId !== data?.taxId || billingContactName !== data?.billingContactName || relationshipPartnerName !== data?.relationshipPartnerName ||
    relationshipPartnerEmail !== data?.relationshipPartnerEmail || zipCode !== data?.zipCode || data?.officeLocations !== officeLocationsData || data?.statePractices != statePracticesData
  ));

  // Office locations management functions
  const handleAddNewOfficeLocation = () => {
    const newLocation = {
      id: officeLocationsData?.length > 0 ? Math.max(...officeLocationsData.map(d => d.id)) + 1 : 1,
      country: "",
      state: "",
      city: "",
      countryCode: 0
    };
    setOfficeLocationsData([newLocation, ...officeLocationsData]);
  };

  const handleOfficeLocationCountryChange = (value, id) => {
    if (value) {
      const updatedData = officeLocationsData.map(entry => {
        if (entry.id === id) {
          return {
            ...entry,
            country: value?.value,
            countryCode: value?.code || 346,
            state: value?.value === "United States" ? "" : "Nationwide",
            city: value?.value === "United States" ? "" : "Statewide"
          };
        }
        return entry;
      });
      setOfficeLocationsData(updatedData);
    }
  };

  const handleOfficeLocationStateChange = (value, id) => {
    if (value) {
      const updatedData = officeLocationsData.map(entry => {
        if (entry.id === id) {
          return {
            ...entry,
            state: value?.value,
            city: ""
          };
        }
        return entry;
      });
      setOfficeLocationsData(updatedData);
    }
  };

  const handleOfficeLocationCityChange = (value, id) => {
      const updatedData = officeLocationsData.map(entry => {
        if (entry.id === id) {
          return {
            ...entry,
            city: value
          };
        }
        return entry;
      });
      setOfficeLocationsData(updatedData);
  };

  const handleDeleteOfficeLocation = (rowId) => {
    const updatedLocations = officeLocationsData.filter(row => row.id !== rowId);
    setOfficeLocationsData(updatedLocations);
  };

  const renderOfficeLocationCell = (location, fieldName) => {
    switch (fieldName) {
      case 'country':
        return (
          <MasterDataAutocomplete
            register={register}
            errors={errors}
            validationRules={organizationValidationRules}
            value={location?.country}
            setValue={(selectedValue) => handleOfficeLocationCountryChange(selectedValue, location?.id)}
            dataType={"country"}
            name={`officeLocationCountry__${location?.id}`}
            id={`officeLocationCountry__${location?.id}`}
            testid="handleInputChange-officeLocationCountry"
            placeholderValue={SELECT_COUNTRY}
            isDependent={false}
            filterCheck={false}
            required={true}
            disablePortal={false}
          />
        );
      case 'state':
        return location.country === "United States" ? (
          <MasterDataAutocomplete
            register={register}
            errors={errors}
            validationRules={organizationValidationRules}
            value={location?.state}
            setValue={(value) => handleOfficeLocationStateChange(value, location?.id)}
            dataType={"state"}
            name={`officeLocationState__${location?.id}`}
            id={`officeLocationState__${location?.id}`}
            testid="handleInputChange-officeLocationState"
            placeholderValue={SELECT_STATE}
            isDependent={false}
            dependentCode={location?.countryCode}
            filterCheck={false}
            required={true}
            disablePortal={false}
          />
        ) : (
          <TextField
            value={location?.country ? "Nationwide" : ""}
            textFieldProps={{
              name: "statePracticesState",
              id: "statePracticesState",
            }}
            fullWidth
            type="text"
            disabled={true}
            required={true}
            data-testid="handleInputChange-statePracticesState-nationwide"
          />
        );
      case 'city':
        if (location?.country !== "United States" || location?.state === "Nationwide") {
          return (
            <TextField
              value={location?.city}
              textFieldProps={{
                name: `officeLocationCity_${location?.id}`,
                id: `officeLocationCity_${location?.id}`,
              }}
              fullWidth={true}
              type="text"
              disabled={true}
              required={true}
            />
          );
        } else {
          return (
            <TextField
              value={location?.city || ''}
              onChange={(e) => handleOfficeLocationCityChange(e.target.value, location?.id)}
              textFieldProps={{
                name: `officeLocationCity_${location?.id}`,
                id: `officeLocationCity_${location?.id}`,
              }}
              type="text"
              fullWidth={true}
              placeholder={location?.state ? "Enter city name" : "Select state first"}
              disabled={!location?.state}
              required={true}
            />
          );
        }

      default:
        return location[fieldName] || '-';
    }
  };

  const renderOfficeLocations = () => {
    let displayData = officeLocationsData;

    // Define columns for MuiDataTable
    const officeLocationColumns = [
      {
        name: "country",
        label: "Country *",
        options: {
          filter: false,
          sort: false,
          searchable: false,
          customBodyRender: (value, tableMeta) => {
            const location = officeLocationsData[tableMeta.rowIndex];
            return renderOfficeLocationCell(location, 'country');
          },
          setCellHeaderProps: () => ({ style: { color: 'black', fontWeight: 'bold' } })
        }
      },
      {
        name: "state",
        label: "State *",
        options: {
          filter: false,
          sort: false,
          searchable: false,
          customBodyRender: (value, tableMeta) => {
            const location = officeLocationsData[tableMeta.rowIndex];
            return renderOfficeLocationCell(location, 'state');
          },
          setCellHeaderProps: () => ({ style: { color: 'black', fontWeight: 'bold' } })
        }
      },
      {
        name: "city",
        label: "City *",
        options: {
          filter: false,
          sort: false,
          searchable: false,
          customBodyRender: (value, tableMeta) => {
            const location = officeLocationsData[tableMeta.rowIndex];
            return renderOfficeLocationCell(location, 'city');
          },
          setCellHeaderProps: () => ({ style: { color: 'black', fontWeight: 'bold' } })
        }
      },
    ];

    officeLocationColumns.push({
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        searchable: false,
        customBodyRender: (value, tableMeta) => {
          const location = displayData[tableMeta.rowIndex];
          const canDelete = officeLocationsData.length > 1;

          return (
            canDelete && <div className={classes.actionsItem}>
              <IconButton
                a11yLabel="Delete"
                size="small"
                data-testid={`office-delete-${tableMeta?.rowIndex}`}
                onClick={() => handleDeleteOfficeLocation(location.id)}
              >
                <Trash size="small" />
              </IconButton>
            </div>
          );
        },
        setCellHeaderProps: () => ({ style: { color: 'black', fontWeight: 'bold' } })
      }
    });

    return (
      <>
        <MuiDataTable
          data={displayData}
          columns={officeLocationColumns}
          customToolbar={<Body as="p" size="small">
            <strong>{OFFICE_LOCATION + ":"}</strong></Body>}
          disableSearch={true}
          disableFilter={true}
          disableExport={true}
          onAddNew={handleAddNewOfficeLocation}
          customOptions={customTableOptions}
          noResultsText={ADD_NEW_OFFICE_LOCATION}
        />
      </>
    );
  }

  // State practices management functions
  const handleAddNewStatePractice = () => {
    const newLocation = {
      id: statePracticesData?.length > 0 ? Math.max(...statePracticesData.map(d => d.id)) + 1 : 1,
      country: "",
      state: "",
      region: []
    };
    setStatePracticesData([newLocation, ...statePracticesData]);
  };

  const handleStatePracticesCountryChange = (value, id) => {
    if (value && statePracticesData.length > 0) {
      const updatedData = statePracticesData.map(entry => {
        if (entry.id === id) {
          return {
            ...entry,
            country: value,
            countryCode: value?.code || 346,
            state: value === "United States" ? [] : ["Nationwide/Statewide"],
            region: []
          };
        }
        return entry;
      });
      setStatePracticesData(updatedData);
    }
  };

  const handleStatePracticesStateChange = (value, id) => {
    if (value && statePracticesData.length > 0) {
      const updatedData = statePracticesData.map(entry => {
        if (entry.id === id) {
          return {
            ...entry,
            state: value,
            region: []
          };
        }
        return entry;
      });
      setStatePracticesData(updatedData);
    }
  };

  const handleStatePracticesRegionChange = (e, value, id) => {
    const selectedOptions = (typeof value === 'string' ? (value?.split(',')) : value);
    if (statePracticesData.length > 0) {
      const updatedData = statePracticesData.map(entry => {
        if (entry.id === id) {
          return {
            ...entry,
            region: selectedOptions
          };
        }
        return entry;
      });
      setStatePracticesData(updatedData);
    }
  }

  const handleDeleteStatePractice = (rowId) => {
    setStatePracticesData(statePracticesData.filter(row => row.id !== rowId));
  };

  const renderStatePracticeLocationCell = (location, fieldName) => {
    switch (fieldName) {
      case 'country':
        return (
          <MasterDataAutocomplete
            value={location?.country}
            setValue={(selectedValue) => handleStatePracticesCountryChange(selectedValue, location?.id)}
            dataType={"country"}
            register={register}
            validationRules={organizationValidationRules}
            name={`statePracticesCountry_${location?.id}`}
            id={`statePracticesCountry_${location?.id}`}
            testid="handleInputChange-statePracticesCountry"
            placeholderValue={SELECT_COUNTRY}
            isDependent={false}
            filterCheck={true}
            required={true}
            disablePortal={false}
          />
        );
      case 'state':
        return location.country === "United States" ? (
          <MasterDataAutocomplete
            value={location?.state}
            setValue={(selectedValue) => handleStatePracticesStateChange(selectedValue?.value, location?.id)}
            register={register}
            validationRules={organizationValidationRules}
            dataType={"state"}
            name={`statePracticesState_${location?.id}`}
            id={`statePracticesState_${location?.id}`}
            testid="handleInputChange-statePracticesState"
            placeholderValue={SELECT_STATE}
            disablePortal={false}
            required={true}
          />
        ) : (
          <TextField
            value={location?.country ? "Nationwide/Statewide" : ""}
            textFieldProps={{
              name: "statePracticesState",
              id: "statePracticesState",
              style: { width: '300px', minWidth: '300px', maxWidth: '300px' }
            }}
            fullWidth={true}
            type="text"
            disabled={true}
            data-testid="handleInputChange-statePracticesState-nationwide"
            required={true}
          />
        );
      case 'region':
        return (
          <MasterDataMultiAutocomplete
            value={location?.region}
            setValue={(e, value) => handleStatePracticesRegionChange(e, value, location?.id)}
            register={register}
            validationRules={organizationValidationRules}
            dataType={"statePracticesRegion"}
            name={`statePracticesRegion_${location?.id}`}
            id={`statePracticesRegion_${location?.id}`}
            testid="handleInputChange-statePracticesRegion"
            placeholderValue={!location?.state ? "Please select state first" : SELECT_REGION}
            disabled={!location?.state}
            disablePortal={false}
            required={!location?.region?.length}
          />
        );
      default:
        return location[fieldName] || '-';
    }
  };

  const renderStatePractices = () => {
    let displayData = statePracticesData;

    // Define columns for MuiDataTable
    const statePracticesColumns = [
      {
        name: "country",
        label: "Country *",
        options: {
          filter: false,
          sort: false,
          searchable: false,
          customBodyRender: (value, tableMeta) => {
            const location = displayData[tableMeta.rowIndex];
            return (
              <>
                {renderStatePracticeLocationCell(location, 'country')}
              </>
            );
          },
          setCellHeaderProps: () => ({ style: { color: 'black', fontWeight: 'bold', minWidth: '250px', maxWidth: '250px', width: '250px' } }),
          setCellProps: () => ({ style: { width: '250px', minWidth: '250px', maxWidth: '250px' } })
        }
      },
      {
        name: "state",
        label: "State *",
        options: {
          filter: false,
          sort: false,
          searchable: false,
          customBodyRender: (value, tableMeta) => {
            const location = displayData[tableMeta.rowIndex];
            return (
              <>
                {renderStatePracticeLocationCell(location, 'state')}
              </>
            );
          },
          setCellHeaderProps: () => ({ style: { color: 'black', fontWeight: 'bold', minWidth: '400px', maxWidth: '400px', width: '400px' } }),
          setCellProps: () => ({ style: { width: '400px', minWidth: '400px', maxWidth: '400px' } })
        }
      },
      {
        name: "region",
        label: "Region *",
        options: {
          filter: false,
          sort: false,
          searchable: false,
          customBodyRender: (value, tableMeta) => {
            const location = displayData[tableMeta.rowIndex];
            return (
              <>
                {renderStatePracticeLocationCell(location, 'region')}
              </>
            );
          },
          setCellHeaderProps: () => ({ style: { color: 'black', fontWeight: 'bold', width: 'auto' } }),
          setCellProps: () => ({ style: { width: 'auto' } })
        }
      }
    ];

    statePracticesColumns.push({
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        searchable: false,
        customBodyRender: (value, tableMeta) => {
          const location = displayData[tableMeta.rowIndex];

          return (
            <div className={classes.actionsItem}>
              <IconButton
                a11yLabel="Delete"
                size="small"
                data-testid={`state-practice-delete-${tableMeta?.rowIndex}`}
                onClick={() => handleDeleteStatePractice(location.id)}
              >
                <Trash size="small" />
              </IconButton>
            </div>
          );
        },
        setCellHeaderProps: () => ({ style: { color: 'black', fontWeight: 'bold', minWidth: '100px', maxWidth: '100px' } }),
        setCellProps: () => ({ style: { width: '100px', minWidth: '100px', maxWidth: '100px' } })
      }
    });

    return (
      <MuiDataTable
        data={displayData}
        columns={statePracticesColumns}
        customToolbar={<Body as="p" size="small">
          <strong>{STATE_PRACTICES + ":"}</strong>
        </Body>}
        disableSearch={true}
        disableFilter={true}
        disableExport={true}
        onAddNew={handleAddNewStatePractice}
        customOptions={customTableOptions}
        noResultsText={ADD_NEW_STATE_PRACTICE}
      />
    );
  }

  return (
    <Detail
      onCancel={handleCancel}
      onUpdate={handleUpdate}
      hideDeleteBtn
      isDirty={isDirty}
      isValid={isValid}
      loading={loading}
    >
      <div className={classes.pageContainer}>
        <div className={classes.editOrganization}>
          Edit Organization Profile
        </div>
        <div className={classes.leftContent}>
          <div className={classes.orgField}>
            <p><strong>Organization Name: </strong><p data-testid="organization-detail-companyName">{companyName}</p></p>
          </div>
          <Grid UNSAFE_style={{ alignItems: 'center' }}>
            <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
              <Label text={FIRM_SIZE} />
              <MasterDataAutocomplete
                value={firmSize}
                setValue={(value) => { handleInputChange("firmSize", value?.value) }}
                dataType={"firmSize"}
                name={"firmSize"}
                id={"firmSize"}
                register={register}
                errors={errors}
                validationRules={organizationValidationRules}
                testid="handleInputChange-firmSize"
                placeholderValue={SELECT_FIRM_SIZE}
              />
            </GridColumn>
            <GridColumn sm={8} md={8} lg={8} UNSAFE_style={{ padding: "15px 15px 0 0 " }}></GridColumn>
            <GridColumn sm={8} md={8} lg={8} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
              <Label text={EXPERTISE} />
              {
                loading ? "-" :
                  <MasterDataMultiAutocomplete
                    value={expertiseArray}
                    dataType={"expertise"}
                    placeholderValue={SELECT_EXPERTISE}
                    setValue={multiSelectHandler}
                    testid="organization-detail-expertise"
                  />
              }
            </GridColumn>
            <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
              <Label text={PRIMARY_EXPERTISE} />
              <Select
                onChange={
                  (e) => {
                    handleInputChange(e.target.name, e.target.value)
                  }}
                value={primaryExpertise}
                name="primaryExpertise"
                data-testid="organization-detail-primaryExpertise"
                fullWidth={true}
              >
                {
                  (expertise !== '') ? expertise?.split(',').sort()?.map((item) => {
                    return <MenuItem key={item} value={item} id={`primaryExpertise-option-${item}`}>{item}</MenuItem>
                  }) : <MenuItem value="">{SELECT_EXPERTISE_FIRST}</MenuItem>
                }
              </Select>
            </GridColumn>
          </Grid>
          <div className={classes.accordionSection}>
            <Body as="p" size="medium">
              <strong className={classes.accordianHeading}>{`Contact`}</strong>
            </Body>
            <Grid UNSAFE_style={{ alignItems: 'center' }}>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Label text={ADDRESS_TYPE} />
                <MasterDataAutocomplete
                  value={addressType}
                  setValue={(value) => { handleInputChange("addressType", value?.value) }}
                  dataType={"organizationAddressType"}
                  name="addressType"
                  testid="organization-detail-addressType"
                  placeholderValue={SELECT_ADDRESS}
                  disabled
                />
              </GridColumn>
              <GridColumn sm={8} md={8} lg={8} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Label text={ADDRESS_LINE_1} />
                <TextField
                  onChange={
                    (e) => {
                      handleInputChange(e.target.name, e.target.value);
                    }}
                  value={addressLine1}
                  name="addressLine1"
                  type="text"
                  data-testid="organization-detail-addressLine1"
                  disabled
                  fullWidth={true}
                />
              </GridColumn>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Label text={ADDRESS_LINE_2} />
                <TextField
                  onChange={
                    (e) => {
                      handleInputChange(e.target.name, e.target.value);
                    }}
                  value={addressLine2}
                  name="addressLine2"
                  type="text"
                  data-testid="organization-detail-addressLine2"
                  disabled
                  fullWidth={true}
                />
              </GridColumn>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Label text={ZIPCODE} />
                <TextField
                  error={errors?.zipCode}
                  helperText={errors?.zipCode && <MuiValidationMessage message={errors?.zipCode?.message} />}
                  onChange={
                    (e) => {
                      handleInputChange(e.target.name, e.target.value);
                    }}
                  {...register(
                    'zipCode',
                    { ...organizationValidationRules?.zipCode, onChange: (e) => { handleInputChange(e.target.name, e.target.value) } }
                  )}
                  value={zipCode}
                  name="zipCode"
                  data-testid="organization-detail-zipcode"
                  disabled
                  fullWidth={true}
                />
              </GridColumn>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}></GridColumn>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Label text={CITY} />
                <TextField
                  onChange={
                    (e) => {
                      handleInputChange(e.target.name, e.target.value);
                    }}
                  value={city}
                  name="city"
                  type="text"
                  data-testid="organization-detail-city"
                  disabled
                  fullWidth={true}
                />
              </GridColumn>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Label text={STATE} />
                <MasterDataAutocomplete
                  value={state}
                  setValue={(value) => { handleInputChange("state", value?.value) }}
                  dataType={"state"}
                  name={"state"}
                  testid="organization-detail-state"
                  placeholderValue={SELECT_STATE}
                  disabled
                />
              </GridColumn>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Label text={COUNTRY} />
                <Select
                  onChange={
                    (e) => {
                      handleInputChange(e.target.name, e.target.value)
                    }}
                  value={country}
                  name="country"
                  data-testid="organization-detail-country"
                  disabled
                  fullWidth={true}
                >
                  <MenuItem value="United States" data-testid="organization-detail-country-option">
                    United States
                  </MenuItem>
                </Select>
              </GridColumn>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Label text={WEBSITE} />
                <TextField
                  onChange={
                    (e) => {
                      handleInputChange(e.target.name, e.target.value);
                    }}
                  value={website}
                  name="website"
                  id="website"
                  type="text"
                  data-testid="organization-detail-website"
                  fullWidth={true}
                />
              </GridColumn>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Label text={FIRM_PHONE_NO} />
                <TextField
                  error={errors?.phoneNo}
                  helperText={errors?.phoneNo && <MuiValidationMessage message={errors?.phoneNo?.message} />}
                  value={formatPhoneNumber(phoneNo)}
                  name="phoneNo"
                  id="phoneNo"
                  type="text"
                  data-testid="organization-detail-phoneNo"
                  {...register(
                    'phoneNo',
                    { ...organizationValidationRules.phoneNo, onChange: (e) => { handleInputChange(e.target.name, formatPhoneNumber(e.target.value)) } }
                  )}
                  fullWidth={true}
                />
              </GridColumn>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Label text={FIRM_FAX_NO} />
                <TextField
                  error={errors?.faxNo}
                  helperText={errors?.faxNo && <MuiValidationMessage message={errors?.faxNo?.message} />}
                  value={faxNo}
                  name="faxNo"
                  type="text"
                  data-testid="organization-detail-faxNo"
                  {...register(
                    'faxNo',
                    { ...organizationValidationRules.faxNo, onChange: (e) => { handleInputChange(e.target.name, e.target.value) } }
                  )}
                  fullWidth={true}
                />
              </GridColumn>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Body as="p" size="small">
                  <strong>{RELATIONSHIP_PARTNER_NAME + ":"}</strong>
                </Body>
                <TextField
                  size={"large"}
                  disabled={true}
                  onChange={
                    (e) => {
                      handleInputChange(e.target.name, e.target.value);
                    }}
                  value={relationshipPartnerName}
                  name="relationshipPartnerName"
                  type="text"
                  data-testid="organization-detail-relationshipPartnerName"
                  fullWidth={true}
                />
              </GridColumn>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Body as="p" size="small">
                  <strong>{RELATIONSHIP_PARTNER_EMAIL + ":"}</strong>
                </Body>
                <TextField
                  disabled={true}
                  onChange={
                    (e) => {
                      handleInputChange(e.target.name, e.target.value);
                    }}
                  value={relationshipPartnerEmail}
                  name="relationshipPartnerEmail"
                  type="text"
                  data-testid="organization-detail-relationshipPartnerEmail"
                  fullWidth={true}
                />
              </GridColumn>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}></GridColumn>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Body as="p" size="small">
                  <strong>{INTERNAL_CONTACT_NAME + ":"}</strong>
                </Body>
                <TextField
                  size={"large"}
                  disabled={true}
                  onChange={
                    (e) => {
                      handleInputChange(e.target.name, e.target.value);
                    }}
                  value={internalContactName}
                  name="internal"
                  type="text"
                  data-testid="organization-detail-internalContactName"
                  fullWidth={true}
                />
              </GridColumn>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Body as="p" size="small">
                  <strong>{INTERNAL_CONTACT_EMAIL + ":"}</strong>
                </Body>
                <TextField
                  disabled={true}
                  onChange={
                    (e) => {
                      handleInputChange(e.target.name, e.target.value);
                    }}
                  value={internalContactEmail}
                  name="internalContactEmail"
                  type="text"
                  data-testid="organization-detail-internalContactEmail"
                  fullWidth={true}
                />
              </GridColumn>
              <GridColumn sm={12} md={12} lg={12} >
                {renderOfficeLocations()}
              </GridColumn>
            </Grid>
          </div>
          <div className={classes.accordionSection}>
            <Body as="p" size="medium">
              <strong className={classes.accordianHeading}>{`Authorization`}</strong>
            </Body>
            <Grid UNSAFE_style={{ alignItem: 'center' }}>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Switch
                  id="mELApproved"
                  data-testid="OrgAdditionInfo-mELApproved"
                  isOn={mELApproved === YES ? true : false}
                  onClick={() => { handleInputChange("mELApproved", mELApproved === YES ? NO : YES) }}
                  label={MEL_APPROVED}
                  disabled
                />
              </GridColumn>
              <GridColumn sm={12} md={12} lg={12} >
                {renderStatePractices()}
              </GridColumn>
            </Grid>
          </div>
          <div className={classes.accordionSection}>
            <Body as="p" size="medium">
              <strong className={classes.accordianHeading}>{`Financial`}</strong>
            </Body>
            <Grid UNSAFE_style={{ alignItem: 'center' }}>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Label text={TAX_ID} />
                <TextField
                  disabled={true}
                  onChange={
                    (e) => {
                      handleInputChange(e.target.name, e.target.value);
                    }}
                  value={taxId}
                  name="taxId"
                  type="text"
                  data-testid="organization-detail-taxId"
                  fullWidth={true}
                />
              </GridColumn>
              <GridColumn sm={4} md={4} lg={4} UNSAFE_style={{ padding: "15px 15px 0 0 " }}>
                <Label text={BILLING_CONTACT_NAME} />
                <TextField
                  disabled={true}
                  onChange={
                    (e) => {
                      handleInputChange(e.target.name, e.target.value);
                    }}
                  value={billingContactName}
                  name="billingContactName"
                  type="text"
                  data-testid="organization-detail-billingContactName"
                  fullWidth={true}
                />
              </GridColumn>
            </Grid>
          </div>
        </div>
      </div>

    </Detail >
  );
};

export default EditOrganization;
